import React from 'react';
import { Box, Button, ButtonGroup, Tooltip, useTheme } from '@mui/material';
import CallEndIcon from '@mui/icons-material/CallEnd';
import ArticleIcon from '@mui/icons-material/Article';
import ClosedCaptionIcon from '@mui/icons-material/ClosedCaption';
import ClosedCaptionOffIcon from '@mui/icons-material/ClosedCaptionOff';
import {
  useDaily,
  useLocalParticipant,
  useScreenShare,
} from '@daily-co/daily-react';

import { useUserInfoContext } from '../../lib/contexts/UserInfoContext';

import {
  CameraOn,
  CameraOff,
  MicrophoneOff,
  MicrophoneOn,
  Screenshare,
} from './Icons';

interface TrayProps {
  leaveCall: (e: React.MouseEvent<HTMLButtonElement>) => void;
  toggleTranscript: () => void;
  isTranscriptVisible: boolean;
  captionsEnabled: boolean;
  toggleCaptions: () => void;
}

export default function Tray({ leaveCall, toggleTranscript, isTranscriptVisible, captionsEnabled, toggleCaptions }: TrayProps) {
  const callObject = useDaily();
  const { isSharingScreen, startScreenShare, stopScreenShare } = useScreenShare();
  const { userInfo, updateUserInfo } = useUserInfoContext();
  const theme = useTheme();

  const localParticipant = useLocalParticipant();
  const mutedVideo = !localParticipant?.video;
  const mutedAudio = !localParticipant?.audio;

  const toggleVideo = () => {
    if (callObject) {
      callObject.setLocalVideo(mutedVideo);
    }
  };

  const toggleAudio = () => {
    if (callObject) {
      callObject.setLocalAudio(mutedAudio);
    }
  };

  const toggleScreenShare = () => (isSharingScreen ? stopScreenShare() : startScreenShare());

  const handleLeaveCall = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (userInfo) {
      userInfo.isInCall = false;
      updateUserInfo({
        ...userInfo,
      });
    }
    leaveCall(e);
  };

  // Base styles for all buttons in the tray
  const baseButtonStyles = {
    borderRadius: '16px',
    width: '56px',
    height: '56px',
    minWidth: '56px',
    padding: 0,
    transition: 'background-color 0.2s, border-color 0.2s, color 0.2s',
    color: theme.palette.text.primary,
    background: theme.palette.mode === 'light' ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.1)',
    border: `1px solid ${theme.palette.mode === 'light' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(255, 255, 255, 0.2)'}`,
    '&:hover': {
      background: theme.palette.mode === 'light' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(255, 255, 255, 0.2)',
      borderColor: theme.palette.mode === 'light' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.3)',
    },
    // Six buttons at 56px (+ padding) are wider than a 360-375px phone; shrink them there.
    '@media (max-width: 420px)': { width: '44px', height: '44px', minWidth: '44px' },
  };

  // Styles for buttons when they are in an "active" or "muted" state
  const activeMutedButtonStyles = {
    ...baseButtonStyles,
    background: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    border: `1px solid ${theme.palette.secondary.dark}`,
    '&:hover': {
      background: theme.palette.secondary.dark,
    },
  };

  // Styles for the hang-up button
  const hangupButtonStyles = {
    ...baseButtonStyles,
    background: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    border: `1px solid ${theme.palette.error.dark}`,
    '&:hover': {
      background: theme.palette.error.dark,
    },
  };

  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: theme.palette.background.paper,
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderRadius: '16px',
        border: `1px solid ${theme.palette.divider}`,
        padding: '10px 20px',
        '@media (max-width: 420px)': { padding: '8px 10px' },
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 10,
        boxShadow: theme.shadows[3],
      }}
    >
      <ButtonGroup variant="contained" aria-label="outlined primary button group" sx={{ '& .MuiButtonGroup-grouped': { border: 'none' } }}>
        <Button sx={mutedVideo ? activeMutedButtonStyles : baseButtonStyles} onClick={toggleVideo}>
          {mutedVideo ? <CameraOff /> : <CameraOn />}
        </Button>
        <Button sx={mutedAudio ? activeMutedButtonStyles : baseButtonStyles} onClick={toggleAudio}>
          {mutedAudio ? <MicrophoneOff /> : <MicrophoneOn />}
        </Button>
        <Button sx={baseButtonStyles} onClick={toggleScreenShare}>
          <Screenshare />
        </Button>
        <Tooltip title="Transcript & feedback">
          <Button
            sx={isTranscriptVisible ? activeMutedButtonStyles : baseButtonStyles}
            onClick={toggleTranscript}
            aria-label="Transcript and feedback"
          >
            <ArticleIcon />
          </Button>
        </Tooltip>
        <Tooltip title={captionsEnabled ? 'Turn captions off' : 'Turn captions on'}>
          <Button
            sx={captionsEnabled ? activeMutedButtonStyles : baseButtonStyles}
            onClick={toggleCaptions}
            aria-label="Captions"
            aria-pressed={captionsEnabled}
          >
            {captionsEnabled ? <ClosedCaptionIcon /> : <ClosedCaptionOffIcon />}
          </Button>
        </Tooltip>
        <Button sx={hangupButtonStyles} onClick={handleLeaveCall}>
          <CallEndIcon />
        </Button>
      </ButtonGroup>
    </Box>
  );
}
